import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { getVisiblePlaces, debounce } from '../../utils/lazyMarkers';
import { hapticFeedback } from '../../utils/hapticFeedback';

interface Place {
  id: number;
  nameMn: string;
  nameEn: string;
  category: string;
  mood: string[];
  budget: number;
  rating: number;
  views: number;
  isOpen: boolean;
  verified: string;
  tip: string;
  description: string;
  hours: string;
  contact: string;
  address: string;
  lat: number;
  lng: number;
  image: string;
  timeOfDay: string[];
  amenities: string[];
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface InteractiveMapProps {
  places: Place[];
  selectedPlace: Place | null;
  onPlaceSelect: (place: Place) => void;
  userLocation: { lat: number; lng: number } | null;
  radiusKm?: number;
  onBoundsChange?: (bounds: MapBounds) => void;
  mapState?: {
    zoom: number;
    center: { lat: number; lng: number };
  };
}

const CATEGORY_ICONS: Record<string, string> = {
  eat: '🍴',
  party: '🍺',
  pub: '🍻',
  explore: '🏛️',
  romantic: '💕',
  coffee: '☕',
  unique: '✨',
  shopping: '🛍️',
  spa: '💆',
  massage: '💆',
};

// Create custom marker icon with emoji and color
function createCustomIcon(emoji: string, isOpen: boolean): L.DivIcon {
  const borderColor = isOpen ? '#4CAF50' : '#EF4444';
  return L.divIcon({
    html: `
      <div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: white;
        border: 3px solid ${borderColor};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        transition: transform 0.3s ease;
      " class="custom-marker">
        ${emoji}
      </div>
    `,
    className: 'custom-marker-wrapper',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

// User location marker icon
function createUserIcon(): L.DivIcon {
  return L.divIcon({
    html: `
      <div style="
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #0066CC;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    className: 'user-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

// Component to handle map events and updates
function MapController({
  places,
  selectedPlace,
  onPlaceSelect,
  userLocation,
  radiusKm,
  onBoundsChange,
  mapState,
  isEnglish,
}: InteractiveMapProps & { isEnglish: boolean }) {
  const map = useMap();
  const markersRef = useRef<Map<number, L.Marker>>(new Map());
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const radiusCircleRef = useRef<L.Circle | null>(null);
  const [currentBounds, setCurrentBounds] = useState<MapBounds | null>(null);

  const visiblePlaces = useMemo(() => {
    if (!currentBounds) return places;
    return getVisiblePlaces(places, currentBounds, 0.2);
  }, [places, currentBounds]);

  // Handle bounds change
  const handleBoundsChanged = useCallback(
    debounce(() => {
      const bounds = map.getBounds();
      const newBounds: MapBounds = {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      };
      setCurrentBounds(newBounds);
      onBoundsChange?.(newBounds);
    }, 300),
    [map, onBoundsChange]
  );

  // Initialize bounds and listeners
  useEffect(() => {
    handleBoundsChanged();
    map.on('moveend', handleBoundsChanged);
    map.on('zoomend', handleBoundsChanged);

    return () => {
      map.off('moveend', handleBoundsChanged);
      map.off('zoomend', handleBoundsChanged);
    };
  }, [map, handleBoundsChanged]);

  // Update map state (zoom/center)
  useEffect(() => {
    if (mapState) {
      map.setView([mapState.center.lat, mapState.center.lng], mapState.zoom);
    }
  }, [map, mapState]);

  // Update markers with clustering
  useEffect(() => {
    // Clear existing cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    // Create new cluster group
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #0066CC;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">${count}</div>`,
          className: 'marker-cluster-custom',
          iconSize: [40, 40],
        });
      },
    });

    clusterGroupRef.current = clusterGroup;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current.clear();

    // Add new markers
    visiblePlaces.forEach((place) => {
      const icon = CATEGORY_ICONS[place.category] ?? '📍';
      const customIcon = createCustomIcon(icon, place.isOpen);

      const marker = L.marker([place.lat, place.lng], { icon: customIcon });

      // Create popup content
      const popupContent = `
        <div style="padding:10px;max-width:220px;font-family:sans-serif;">
          <img src="${place.image}" alt="${isEnglish ? place.nameEn : place.nameMn}"
            style="width:100%;height:100px;object-fit:cover;border-radius:8px;margin-bottom:8px;" />
          <h3 style="margin:0 0 6px;font-weight:700;font-size:14px;color:#111;">
            ${isEnglish ? place.nameEn : place.nameMn}
          </h3>
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="color:#FFD700;font-size:13px;">★ ${place.rating}</span>
            <span style="color:#888;font-size:12px;">${'$'.repeat(place.budget)}</span>
            <span style="font-size:12px;">${icon} ${place.category}</span>
          </div>
          <div style="font-size:12px;font-weight:600;color:${place.isOpen ? '#4CAF50' : '#EF4444'};">
            ${place.isOpen
              ? (isEnglish ? '● Open Now' : '● Нээлттэй')
              : (isEnglish ? '● Closed' : '● Хаалттай')}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent);

      // Click handler
      marker.on('click', () => {
        hapticFeedback.tap();
        onPlaceSelect(place);
        map.panTo([place.lat, place.lng]);
      });

      // Hover animation
      marker.on('mouseover', (e) => {
        const markerElement = e.target.getElement();
        if (markerElement) {
          const innerDiv = markerElement.querySelector('.custom-marker') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.transform = 'scale(1.2)';
          }
        }
      });

      marker.on('mouseout', (e) => {
        const markerElement = e.target.getElement();
        if (markerElement) {
          const innerDiv = markerElement.querySelector('.custom-marker') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.transform = 'scale(1)';
          }
        }
      });

      clusterGroup.addLayer(marker);
      markersRef.current.set(place.id, marker);
    });

    map.addLayer(clusterGroup);

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [visiblePlaces, isEnglish, onPlaceSelect, map]);

  // Highlight selected place
  useEffect(() => {
    if (!selectedPlace) return;
    const marker = markersRef.current.get(selectedPlace.id);
    if (marker) {
      map.panTo([selectedPlace.lat, selectedPlace.lng]);
      marker.openPopup();
      
      // Bounce animation
      const markerElement = marker.getElement();
      if (markerElement) {
        const innerDiv = markerElement.querySelector('.custom-marker') as HTMLElement;
        if (innerDiv) {
          innerDiv.style.animation = 'bounce 0.7s ease';
          setTimeout(() => {
            innerDiv.style.animation = '';
          }, 700);
        }
      }
    }
  }, [selectedPlace, map]);

  // User location marker & radius circle
  useEffect(() => {
    // Remove old user marker and circle
    if (userMarkerRef.current) {
      map.removeLayer(userMarkerRef.current);
      userMarkerRef.current = null;
    }
    if (radiusCircleRef.current) {
      map.removeLayer(radiusCircleRef.current);
      radiusCircleRef.current = null;
    }

    if (!userLocation) return;

    // Add user marker
    const userIcon = createUserIcon();
    userMarkerRef.current = L.marker([userLocation.lat, userLocation.lng], {
      icon: userIcon,
      zIndexOffset: 1000,
    }).addTo(map);

    userMarkerRef.current.bindPopup(isEnglish ? 'Your Location' : 'Таны байршил');

    // Add radius circle
    if (radiusKm) {
      radiusCircleRef.current = L.circle([userLocation.lat, userLocation.lng], {
        radius: radiusKm * 1000,
        color: '#0066CC',
        fillColor: '#0066CC',
        fillOpacity: 0.08,
        weight: 2,
        opacity: 0.6,
      }).addTo(map);
    }

    map.panTo([userLocation.lat, userLocation.lng]);
  }, [userLocation, radiusKm, map, isEnglish]);

  // Sidebar events
  useEffect(() => {
    const handleSidebarHover = (event: Event) => {
      const { placeId } = (event as CustomEvent).detail;
      const marker = markersRef.current.get(placeId);
      if (marker) {
        const markerElement = marker.getElement();
        if (markerElement) {
          const innerDiv = markerElement.querySelector('.custom-marker') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.animation = 'bounce 0.7s ease';
            setTimeout(() => {
              innerDiv.style.animation = '';
            }, 700);
          }
        }
      }
    };

    const handleSidebarClick = (event: Event) => {
      const { place } = (event as CustomEvent).detail;
      if (place) {
        map.setView([place.lat, place.lng], 16);
        const marker = markersRef.current.get(place.id);
        if (marker) {
          marker.openPopup();
        }
      }
    };

    window.addEventListener('sidebar-place-hover', handleSidebarHover);
    window.addEventListener('sidebar-place-click', handleSidebarClick);
    return () => {
      window.removeEventListener('sidebar-place-hover', handleSidebarHover);
      window.removeEventListener('sidebar-place-click', handleSidebarClick);
    };
  }, [map]);

  return null;
}

export default function InteractiveMap(props: InteractiveMapProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const initialCenter = props.mapState?.center ?? { lat: 47.9192, lng: 106.9177 };
  const initialZoom = props.mapState?.zoom ?? 13;

  return (
    <div className="w-full h-full relative">
      <MapContainer
        center={[initialCenter.lat, initialCenter.lng]}
        zoom={initialZoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController {...props} isEnglish={isEnglish} />
      </MapContainer>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-10px) scale(1.1); }
          50% { transform: translateY(-5px) scale(1.05); }
          75% { transform: translateY(-8px) scale(1.08); }
        }

        .custom-marker-wrapper {
          background: transparent !important;
          border: none !important;
        }

        .marker-cluster-custom {
          background: transparent !important;
          border: none !important;
        }

        .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .leaflet-popup-content {
          margin: 0;
          width: auto !important;
        }

        .leaflet-popup-tip {
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>
    </div>
  );
}